import {
  CoverageWithPricesInfo,
  ElementTracker,
  ElementTrackerType,
  eligibilityClient,
  MembershipType,
  ProgramType,
} from '@enaratech/funnel-helper';
import { CircularProgress, InputAdornment, TextField } from '@mui/material';
import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react';
import CircleCancelIcon from 'src/components/Common/Icons/CircleCancelIcon';
import CircleSelectionCheckedIcon from 'src/components/Common/Icons/CircleSelectionCheckedIcon';
import { useRoutePath } from 'src/hooks/useRoutePath';
import './scss/promoInput.scss';

const DEBOUNCE_DELAY = 1000;

type Props = {
  handleChange: (couponCode: string | null, data: CoverageWithPricesInfo | null) => void;
  clinicId: number;
  membershipType: MembershipType | null;
  programType: ProgramType;
};

const PromoInput: FC<Props> = ({ handleChange, clinicId, membershipType, programType }) => {
  const [couponCode, setCouponCode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const abortControllerRef = useRef<AbortController>();
  const lastRequestRef = useRef<string>('');

  const routePath = useRoutePath();

  const applyCoupon = useCallback(
    async (code: string) => {
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();
      lastRequestRef.current = code;

      try {
        const coverage = await eligibilityClient.getCoverage({
          clinicId,
          membershipType: membershipType || MembershipType.Private,
          programType,
          couponCode: code,
        });

        // Only update if this is still the latest request
        if (lastRequestRef.current === code) {
          if (!coverage?.success) {
            setError('You entered an invalid promo code');
            handleChange(null, null);
          } else {
            setError(null);
            handleChange(code, coverage);
          }
        }
        setIsLoading(false);
      } catch (error: unknown) {
        console.error((error as Error).message);

        setError('Failed to validate promo code');
        setIsLoading(false);
      }
    },
    [clinicId, membershipType, programType, handleChange]
  );

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const code = event.target.value.toUpperCase();

      setCouponCode(code);
      setError(null);
      setIsLoading(true);

      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new debounce timer
      debounceTimerRef.current = setTimeout(() => {
        applyCoupon(code);
      }, DEBOUNCE_DELAY);
    },
    [applyCoupon]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <ElementTracker routePath={routePath} name='promo' type={ElementTrackerType.Blurrable}>
      <TextField
        label='Promo'
        variant='filled'
        id='promo-input'
        name='promo'
        type='text'
        className='promo-code'
        value={couponCode}
        onChange={onChange}
        error={!!error}
        helperText={error}
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              {(() => {
                if (isLoading) {
                  return <CircularProgress size={24} className='circular-progress-promo' />;
                }

                if (couponCode && error) {
                  return <CircleCancelIcon color='error' />;
                }

                if (couponCode) {
                  return <CircleSelectionCheckedIcon color='success' />;
                }
              })()}
            </InputAdornment>
          ),
        }}
      />
    </ElementTracker>
  );
};

export default PromoInput;
